import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus, CalendarMinus } from 'lucide-react'
import type { WorkerTableRow } from '../../roster/index.js'
import type { ClinicalBridgeStatus } from '../../schedule/index.js'
import { ADMIN_API_BASE_URL, Button, Skeleton } from '../../core/index.js'
import { enrollBridgeDate, getWorkerClinicalBridge, withdrawBridgeDate, type PartnerSession } from '../lib/partnerApi.js'
import { ErrorNote, INPUT_CLASS, Labeled, SuccessNote, describeError } from './formControls.js'

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const AUTHOR_TEXT: Record<ClinicalBridgeStatus['preBidDates'][number]['enrolledBy'], string> = {
  worker: 'the worker, from their app',
  partner: 'the scheduler',
  import: "the facility's import",
}

interface BridgeEnrollmentFormProps {
  session: PartnerSession
  workers: WorkerTableRow[]
}

/**
 * Plays a scheduler that offers Clinical Bridge in its bid screen (plan
 * D12): records a day the worker chose there with `PUT
 * /workers/{workerId}/clinical-bridge/dates/{date}` as the partner, takes
 * one back with `DELETE`, and shows the record as `GET
 * /workers/{workerId}/clinical-bridge` answers it. Needs `events:write` on
 * the token, Clinical Bridge switched on at the facility and the worker made
 * eligible by staff.
 */
export function BridgeEnrollmentForm({ session, workers }: BridgeEnrollmentFormProps) {
  const queryClient = useQueryClient()
  const [workerId, setWorkerId] = useState('')
  const [date, setDate] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const recordKey = ['partner-simulator', 'clinical-bridge', session.clientId, workerId]
  const recordQuery = useQuery({
    queryKey: recordKey,
    queryFn: () => getWorkerClinicalBridge(ADMIN_API_BASE_URL ?? '', session, workerId),
    enabled: workerId !== '' && Boolean(ADMIN_API_BASE_URL),
  })
  const canSend = workerId !== '' && DATE_PATTERN.test(date) && !isSending

  async function send(action: 'enroll' | 'withdraw'): Promise<void> {
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setDone(null)
    try {
      const call = action === 'enroll' ? enrollBridgeDate : withdrawBridgeDate
      const record = await call(ADMIN_API_BASE_URL, session, workerId, date)
      queryClient.setQueryData(recordKey, record)
      setDone(action === 'enroll' ? `Recorded ${date} as a day the worker would cover.` : `Took ${date} off the worker's list.`)
    } catch (err) {
      setError(describeError(err, action === 'enroll' ? 'The day could not be recorded.' : 'The day could not be withdrawn.'))
    } finally {
      setIsSending(false)
    }
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    void send('enroll')
  }

  const record = recordQuery.data ?? null

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-xs text-ink-mid">
        The scheduler offers Clinical Bridge in its bid screen and records the days a worker chooses there, on the worker's behalf, the way it reports their shifts. The
        day must be today or later in the facility's zone and within this scoring period or the next; the facility must have Clinical Bridge on and staff must have made
        the worker eligible.
      </p>
      <Labeled label="Worker" htmlFor="bridge-worker">
        <select id="bridge-worker" value={workerId} onChange={(event) => setWorkerId(event.target.value)} className={INPUT_CLASS}>
          <option value="">Choose a worker…</option>
          {workers.map((worker) => (
            <option key={worker.workerId} value={worker.workerId}>
              {worker.name} · {worker.role} · {worker.workerId}
            </option>
          ))}
        </select>
      </Labeled>
      <Labeled label="Day" hint="YYYY-MM-DD, in the facility's zone" htmlFor="bridge-date">
        <input id="bridge-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} className={INPUT_CLASS} />
      </Labeled>
      <div className="flex flex-wrap gap-3">
        <Button type="submit" icon={CalendarPlus} disabled={!canSend}>
          {isSending ? 'Sending…' : 'Enroll the worker'}
        </Button>
        <Button type="button" variant="secondary" icon={CalendarMinus} disabled={!canSend} onClick={() => void send('withdraw')}>
          Withdraw the day
        </Button>
      </div>
      {error && <ErrorNote message={error} />}
      {done && <SuccessNote>{done}</SuccessNote>}

      {workerId !== '' && (
        <div className="border-t border-marine/15 pt-4">
          <div className="mb-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">The worker's record</div>
          {recordQuery.isLoading ? (
            <Skeleton />
          ) : recordQuery.isError ? (
            <ErrorNote message={describeError(recordQuery.error, "The worker's record could not be read.")} onRetry={() => void recordQuery.refetch()} retrying={recordQuery.isFetching} />
          ) : record ? (
            <div className="text-xs text-ink">
              <p>
                {record.eligible ? 'Eligible for Clinical Bridge' : 'Not yet made eligible by staff'} · Clinical Bridge is {record.rules.enabled ? 'on' : 'off'} at the facility
                {record.rules.scoreBoostPerShift > 0 && <> · {record.rules.scoreBoostPerShift} points per shift worked</>}
              </p>
              {record.preBidDates.length === 0 ? (
                <p className="mt-1 text-ink-mid">No days on the list.</p>
              ) : (
                <ul className="mt-1 space-y-0.5">
                  {record.preBidDates.map((preBid) => (
                    <li key={preBid.date}>
                      <span className="font-mono">{preBid.date}</span> <span className="text-ink-mid">· put there by {AUTHOR_TEXT[preBid.enrolledBy]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>
      )}
    </form>
  )
}
